import { IPromise, IHttpService, IQService } from "angular";
import teamColors from "wwwroot/json/team-colors.json";

import { EnvironmentService } from "@gtmhub/env";
import {
  IGtmhubToWorkdayMapping,
  IDeltaHistorySyncs,
  IOrganization,
  IOrganizationItems,
  IOrganizationTeamMapping,
  IsEnabled,
  IWorkdaySettings,
  IWorkdayWorker,
  IWorkdayWorkerList,
  WorkdayMappingStatus,
  IWorkdayMappingRun,
  IWorkdayEntityTableMapping,
  ISelection,
  IEntry,
  IEntries,
  SetupStep,
  ConnectionHealth,
  CalculateSetupStepArgs,
  IDataAvailable,
  IDeltaRun,
  IDeltaRunResponse,
  INewWorkdayMappingRun,
  DefaultQueryParams,
  IGetOrganizationsParams,
} from "./models";
import { ITeamsIdMap } from "@gtmhub/teams";
import { localize } from "@gtmhub/localization";

export class WorkdayUserProvisioningService {
  public static $inject = ["$http", "EnvironmentService", "$q"];

  constructor(private $http: IHttpService, private env: EnvironmentService, private $q: IQService) {}

  public checkIfWorkdayProvisioningIsEnabled = (): IPromise<IsEnabled> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/enable");
    return this.$http.get<IsEnabled>(url).then((response) => response.data);
  };

  public enableWorkdayUserProvisioning = (): IPromise<IsEnabled> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/enable");
    return this.$http.put<IsEnabled>(url, null).then((response) => response.data);
  };

  public getOrganizations = (params: IGetOrganizationsParams): IPromise<IOrganizationItems> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/organizations");
    return this.$http.get<IOrganizationItems>(url, { params }).then((response) => response.data);
  };

  public getWorkerUserMappings = (): IPromise<IGtmhubToWorkdayMapping> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/mappings/users");
    return this.$http.get<IGtmhubToWorkdayMapping>(url).then((response) => response.data);
  };

  public getOrganizationTeamMapping = (): IPromise<IGtmhubToWorkdayMapping> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/mappings/teams");
    return this.$http.get<IGtmhubToWorkdayMapping>(url).then((response) => response.data);
  };

  public getMappedWorkdayWorkers = (): IPromise<IWorkdayWorker[]> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/workers");
    return this.$http.get<IWorkdayWorkerList>(url, { params: { includeOnlyMappedWorkers: true } }).then((response) => response.data.items);
  };

  /**
   * Returns promise updated workdaySettings.
   * @param {IWorkdaySettings} workdaySettings we want to update
   * if workdaySettings.syncSettings.organizationIds = ["*"] all organizations will be provisioned
   * @return {IPromise<IWorkdaySettings>}
   */
  public updateWorkdaySettings = (workdaySettings: IWorkdaySettings): IPromise<IWorkdaySettings> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/settings");
    return this.$http.patch<IWorkdaySettings>(url, workdaySettings).then((response) => response.data);
  };

  public postSelection = (selection: ISelection): IPromise<ISelection> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/selections");
    return this.$http.post<ISelection>(url, selection).then((response) => response.data);
  };

  public deleteSelection = (selectionId: string): IPromise<void> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/selections/" + selectionId);
    return this.$http.delete<void>(url).then((response) => response.data);
  };

  public getSelection = (selectionId: string): IPromise<ISelection> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/selections/" + selectionId);
    return this.$http.get<ISelection>(url).then((response) => response.data);
  };

  public getJobLevels = (): IPromise<IEntry[]> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/job-levels");
    return this.$http.get<IEntries>(url).then((response) =>
      //checks if job level ID is no_job_level and if it is its name is translated
      response.data.items.map((entry) => {
        if (entry.id === "no_job_levels") {
          entry.name = localize("no_job_levels");
        }
        return entry;
      })
    );
  };

  public getMappingRun = (mappingRunId: string): IPromise<IWorkdayMappingRun> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/mappings/runs/" + mappingRunId);
    return this.$http.get<IWorkdayMappingRun>(url).then((response) => response.data);
  };

  public runMapping = (): IPromise<INewWorkdayMappingRun> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/mappings/runs");
    return this.$http.post<INewWorkdayMappingRun>(url, null).then((response) => response.data);
  };

  public runDelta = (): IPromise<void> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/deltas/runs");
    return this.$http.post<void>(url, null).then((response) => response.data);
  };

  public getDeltaRuns(params: DefaultQueryParams): IPromise<IDeltaRun[]> {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/deltas/runs");
    return this.$http.get<IDeltaRunResponse>(url, { params }).then((response) => response.data.items);
  }

  public getDeltaRun(deltaId: string): IPromise<IDeltaRun> {
    const url = this.env.getWorkdayUserProvisioningEndpoint(`/deltas/runs/${deltaId}`);
    return this.$http.get<IDeltaRun>(url).then((response) => response.data);
  }

  public mapSingleTeamToOrganization = (teamId: string, organizationId: string): IPromise<void> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint(`/mappings/teams/${teamId}`);
    return this.$http.put<void>(url, { WorkdayEntityID: organizationId }).then((response) => response.data);
  };

  public changeMappingStatus = (approvalValue: boolean): IPromise<void> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/mappings/approve");
    return this.$http.put<void>(url, { approvalValue }).then((response) => response.data);
  };

  public mappingApproved = (): IPromise<WorkdayMappingStatus> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/mappings/approved");
    return this.$http.get<WorkdayMappingStatus>(url).then((response) => response.data);
  };

  public getWorkdayUserProvisionSettings = (): IPromise<IWorkdaySettings> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/settings");
    return this.$http.get<IWorkdaySettings>(url).then((response) => response.data);
  };

  public getAccountSelections = (): IPromise<ISelection[]> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/selections");
    return this.$http.get<ISelection[]>(url).then((response) => response.data);
  };

  public updateSelection = (selection: ISelection): IPromise<void> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/selections/" + selection.id);
    return this.$http.patch<void>(url, selection).then((response) => response.data);
  };

  public putWorkdayUserProvisionSettings = (workdaySettings: IWorkdaySettings): IPromise<IWorkdaySettings> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/settings");
    return this.$http.put<IWorkdaySettings>(url, workdaySettings).then((response) => response.data);
  };

  public deleteTeamToOrganizationMapping = (teamId: string): IPromise<void> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint(`/mappings/teams/${teamId}`);
    return this.$http.delete<void>(url).then((response) => response.data);
  };

  public getDeltaHistorySyncs = (): IPromise<IDeltaHistorySyncs> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/deltas/history-syncs");
    return this.$http.get<IDeltaHistorySyncs>(url).then((response) => response.data);
  };

  public getEntityTableMappings = (): IPromise<IWorkdayEntityTableMapping[]> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/entity-mappings");
    return this.$http.get<IWorkdayEntityTableMapping[]>(url).then((response) => response.data);
  };

  public checkConnectorHealth = (): IPromise<ConnectionHealth> => {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/healthy-connector");
    return this.$http.get<ConnectionHealth>(url).then((response) => response.data);
  };

  public dataAvailable(): IPromise<IDataAvailable> {
    const url = this.env.getWorkdayUserProvisioningEndpoint("/data-available");
    return this.$http.get<IDataAvailable>(url).then((response) => response.data);
  }

  public assignOrganizationColorToName = (orgName: string): string => {
    let hash = 0;
    if (orgName.length === 0) return hash.toString();
    for (let i = 0; i < orgName.length; i++) {
      hash = orgName.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    hash = ((hash % teamColors.length) + teamColors.length) % teamColors.length;
    return teamColors[hash];
  };

  public isHexColorLight = (hexColor: string): boolean => {
    if (hexColor) {
      hexColor = hexColor.replace("#", "");
      const r = parseInt(hexColor.substr(0, 2), 16);
      const g = parseInt(hexColor.substr(2, 2), 16);
      const b = parseInt(hexColor.substr(4, 2), 16);
      const yiq = (r * 299 + g * 587 + b * 114) / 1000;
      return yiq >= 176;
    } else {
      return false;
    }
  };

  public nameToInitials = (name: string): string => {
    const namesArray = name
      .trim()
      .replace(/[^A-Za-z ]+/g, "")
      .split(" ");
    if (namesArray.length === 1) return `${namesArray[0].charAt(0)}`;
    return `${namesArray[0].charAt(0)}${namesArray[namesArray.length - 1].charAt(0)}`;
  };

  public mapTeamsToOrganizationTeamMappings = (teams: ITeamsIdMap, organizationTeamMappings: IOrganizationTeamMapping[]): void => {
    for (const teamId in teams) {
      organizationTeamMappings.push({
        gtmhubTeamId: teamId,
        gtmhubTeamName: teams[teamId].name,
        gtmhubTeamInitials: this.nameToInitials(teams[teamId].name),
        gtmhubTeamColor: teams[teamId].color,
      });
    }
  };
  public mapTeamAndOrganizationNameToId = (
    organizationTeamMappingsRecord: Record<string, string>,
    organizationTeamMappings: IOrganizationTeamMapping[],
    organizations: IOrganization[]
  ): void => {
    for (const teamId in organizationTeamMappingsRecord) {
      const mapping = organizationTeamMappings.find((organizationTeamMapping) => organizationTeamMapping.gtmhubTeamId === teamId);
      if (mapping) {
        mapping.workdayOrganizationId = organizationTeamMappingsRecord[teamId];
        this.mapOrganizationIdToOrganizationName(mapping, organizations);
      }
    }
  };

  returnMappingRunPromise(unfinishedSetupMappingId: string): IPromise<IWorkdayMappingRun> {
    if (unfinishedSetupMappingId) {
      return this.getMappingRun(unfinishedSetupMappingId);
    }
  }

  public getCurrentSetupProgressStep = (): Promise<SetupStep> => {
    let workdaySettings;
    return this.getWorkdayUserProvisionSettings()
      .then((response) => {
        workdaySettings = response;
        return this.$q.all([
          this.checkConnectorHealth(),
          this.dataAvailable(),
          this.getAccountSelections(),
          this.returnMappingRunPromise(workdaySettings.unfinishedSetupMappingId),
          this.mappingApproved(),
        ]);
      })
      .then((response) => {
        const [{ healthy }, { available }, selections, unfinishedSetupMapping, { allMappingsApproved }] = response;
        const calculateSetupStepArgs: CalculateSetupStepArgs = {
          workdaySettings: workdaySettings,
          isConnectorHealthy: healthy,
          syncInProgress: !available,
          selections: selections,
          unfinishedSetupMapping: unfinishedSetupMapping,
          mappingApproved: allMappingsApproved,
        };
        return this.calculateCurrentStep(calculateSetupStepArgs);
      })
      .catch((error) => {
        if (!workdaySettings) {
          error.workdaySettingsDoesNotExists = true;
        }
        throw error;
      });
  };

  public calculateCurrentStep = (args: CalculateSetupStepArgs): SetupStep => {
    if (!args.isConnectorHealthy || !args.workdaySettings?.dataSourceSettings?.connectorId) {
      return SetupStep.SET_UP;
    } else if (args.syncInProgress) {
      return SetupStep.DATA_SYNC;
    } else if (!args.selections || args.selections?.length === 0) {
      return SetupStep.DATA_SYNCED;
    } else if (!args.mappingApproved) {
      if (!args.unfinishedSetupMapping || args.unfinishedSetupMapping.status === "failed") {
        return SetupStep.SELECTION;
      }
      return SetupStep.MAPPING;
    }
    return SetupStep.COMPLETE;
  };

  mapOrganizationIdToOrganizationName = (organizationTeamMapping: IOrganizationTeamMapping, organizations: IOrganization[]): void => {
    const organization = organizations.find((organization) => organization.id == organizationTeamMapping.workdayOrganizationId);
    if (organization) {
      organizationTeamMapping.workdayOrganizationName = organization.name;
      organizationTeamMapping.workdayOrganizationInitials = this.nameToInitials(organization.name);
      organizationTeamMapping.workdayOrganizationColor = this.assignOrganizationColorToName(organization.name);
    }
  };
}
